// Sign-in with WarmHub, through AuthKit, for the Console and Curator Studio.
// The Console holds the access token in memory and sends it as a bearer token
// to the Consumer API, which verifies it; Curator Studio does the same for the
// Curator API. See Design > Console, "Sign-in", and Design > Exchange, "Bearer
// tokens".
//
// AuthKit keeps the refresh token for the session: in a first-party cookie when
// the environment uses a custom auth domain, and in localStorage in `devMode`,
// which an environment on a WorkOS-hosted domain needs. Neither app reads or
// stores a token itself.

import { createClient } from "@workos-inc/authkit-js";

export type AuthkitConfig = {
  clientId: string;
  /** The AuthKit domain: a WorkOS-hosted one in dev, WarmHub's own in production. */
  apiHostname: string;
  /** Keeps the refresh token in localStorage, for an AuthKit domain that isn't first-party. */
  devMode: boolean;
};

export type AuthUser = { id: string; email: string };

export type Auth = {
  user(): AuthUser | null;
  /** A current access token, refreshed if needed, or null when signed out. */
  token(): Promise<string | null>;
  signIn(): Promise<void>;
  signOut(): void;
};

export const CALLBACK_PATH = "/callback";

/**
 * Whether the Exchange refused the account itself, not the request or the
 * token: a WarmHub user outside the operator org, or a WorkOS user with no
 * WarmHub user. Signing in again as the same account can't help, so both apps
 * show a refusal with sign-out as the primary action.
 */
export const isRefusedSession = (e: { _tag: string; reason?: string | undefined }) =>
  e._tag === "NotOperatorOrgMember" || e.reason === "NoWarmHubUser";

/**
 * Starts AuthKit. When the page is the redirect back from AuthKit, this
 * finishes the sign-in first, then puts the address bar back at `home`, the
 * app's home page. `onSignedOut` runs if the session can't be refreshed.
 */
export async function createAuth(cfg: AuthkitConfig, origin: string, onSignedOut: () => void, home = "/#/home"): Promise<Auth> {
  const client = await createClient(cfg.clientId, {
    apiHostname: cfg.apiHostname,
    redirectUri: `${origin}${CALLBACK_PATH}`,
    devMode: cfg.devMode,
    onRedirectCallback: () => { history.replaceState(null, "", home); },
    onRefreshFailure: () => onSignedOut(),
  });
  return {
    user() {
      const u = client.getUser();
      return u ? { id: u.id, email: u.email } : null;
    },
    async token() {
      if (!client.getUser()) return null;
      try { return await client.getAccessToken(); } catch { return null; }
    },
    signIn: () => client.signIn(),
    signOut: () => { try { client.signOut({ returnTo: `${origin}/` }); } catch { onSignedOut(); } },
  };
}
